import { useMsal } from "@azure/msal-react";
import { Card, Divider, Flex, Group, Modal, Stack, Switch, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import { useFeature } from "flagged";
import { cloneElement, memo, type ReactElement, useState } from "react";
import { postAPI } from "src/utils/fetch";
import { db } from "../db";
import { DeleteChatsModal } from "./DeleteChatsModal";

export const SettingsModal = memo(function SettingsModal({ children }: { children: ReactElement }) {
    const is_memory_flag_enabled = useFeature("ENABLE_MEMORY_FLAG");
    const { instance } = useMsal();
    const [opened, { open, close }] = useDisclosure(false);
    const chatsCount = useLiveQuery(async () => {
        return (await db.chats.toArray()).length;
    }, []);
    const messagesCount = useLiveQuery(async () => {
        return (await db.messages.toArray()).length;
    }, []);
    const [memoryEnabled, setMemoryEnabled] = useState(() => {
        const stored = localStorage.getItem("memoryEnabled");
        return stored === "true";
    });
    const queryClient = useQueryClient();
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
    const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.checked;
        setMemoryEnabled(value);
        localStorage.setItem("memoryEnabled", value.toString());
        const storedPreferences = localStorage.getItem("chatPreferences");
        const chatPreferences = storedPreferences
            ? JSON.parse(storedPreferences)
            : {
                  selectedProductLines: [],
                  selectedDatasources: [],
              };
        const product_line =
            chatPreferences.selectedProductLines.length > 1
                ? chatPreferences.selectedProductLines[0]
                : "";
        try {
            await postAPI({
                url: `${BACKEND_URL}/v1/user/preferences`,
                payload: {
                    memory_enabled: value,
                    product_line: product_line,
                    datasources: chatPreferences.selectedDatasources,
                },
                instance,
            });
            await queryClient.invalidateQueries({ queryKey: ["user"] });
        } catch (error) {
            console.error("Error updating user preferences: ", error);
        }
    };
    return (
        <>
            {cloneElement(children, { onClick: open } as React.HTMLAttributes<HTMLElement>)}
            <Modal
                opened={opened}
                onClose={close}
                title="Settings"
                size="lg"
                withinPortal
                keepMounted
            >
                <Stack>
                    {is_memory_flag_enabled ? (
                        <>
                            <Text size="md" fw={700}>
                                Memory
                            </Text>
                            <Switch
                                style={{ marginBottom: "1rem" }}
                                label="Enable memory"
                                checked={memoryEnabled}
                                onChange={handleToggle}
                            />
                            <Divider my="md" />
                        </>
                    ) : null}

                    {/* Database Section */}
                    <Text size="md" fw={700}>
                        Database
                    </Text>
                    <Flex>
                        <Card withBorder style={{ flex: 1 }}>
                            <Text size="xl" ta="center">
                                {chatsCount}
                            </Text>
                            <Text size="xs" c="dimmed" ta="center" tt="uppercase" fw={700}>
                                Chats
                            </Text>
                        </Card>
                        <Card withBorder style={{ flex: 1, marginLeft: -1 }}>
                            <Text size="xl" ta="center">
                                {messagesCount}
                            </Text>
                            <Text size="xs" c="dimmed" ta="center" tt="uppercase" fw={700}>
                                Messages
                            </Text>
                        </Card>
                    </Flex>
                    <Group>
                        <DeleteChatsModal onOpen={close} />
                    </Group>
                </Stack>
            </Modal>
        </>
    );
});
